import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  ComposedChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtPeriod, applyMult } from "./theme";
import {
  KpiCard, SkeletonCard, NoData, SourceChip, SectionTitle, SrTable, DeltaBadge, ChartTooltipContent, BreakBadge, NoteBadge } from "./components";
import PulseLayout from "./PulseLayout";
import SeasonalityView from "./SeasonalityView";
import OriginMixSection from "./OriginMixSection";
import { pulseGet } from "./api";
import { withForecastRows, forecastLayers, ForecastTooltip, ForecastNote } from "./forecast";

const regionName = (o, lang) => {
  if (!o.iso2) return o.label;
  try { return new Intl.DisplayNames([lang], { type: "region" }).of(o.iso2) || o.label; } catch { return o.label; }
};

export default function CountryPulse() {
  const { iso3: rawIso } = useParams();
  const iso3 = (rawIso || "").toUpperCase();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") === "seasonality" ? "seasonality" : "overview";
  const [dm] = useDarkMode();
  const { t, lang, withLang } = usePulseLocale();
  const T = tokens(dm);
  const [kpi, setKpi] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [markets, setMarkets] = useState(null);
  const [forecasts, setForecasts] = useState({});
  const [monthlyInd, setMonthlyInd] = useState("nights_total");
  const [loading, setLoading] = useState(true);
  const forecast = forecasts[monthlyInd] || null;
  const [notFound, setNotFound] = useState(false);
  const [rcSrc, setRcSrc] = useState("usd");

  useEffect(() => {
    let on = true;
    setLoading(true); setNotFound(false); setKpi(null); setMonthly(null); setMarkets(null); setForecasts({}); setMonthlyInd("nights_total");
    Promise.allSettled([
      pulseGet(`kpi/${iso3}`, { lang }),
      pulseGet(`monthly/${iso3}`),
      pulseGet(`markets/${iso3}`),
      pulseGet("forecast", { indicator: "nights_total", country: iso3, lang }),
    ]).then(([k, m, mk, fc]) => {
      if (!on) return;
      if (k.status === "fulfilled") setKpi(k.value);
      else if (k.reason?.response?.status === 404) setNotFound(true);
      if (m.status === "fulfilled") setMonthly(m.value);
      if (mk.status === "fulfilled") setMarkets(mk.value);
      if (fc.status === "fulfilled") setForecasts({ nights_total: fc.value?.forecast || null });
      setLoading(false);
    });
    return () => { on = false; };
  }, [iso3, lang]);

  const k = kpi?.kpis || {};
  const sparks = kpi?.sparklines || {};
  const label = kpi?.country_label || iso3;

  // forecast of the toggled monthly indicator (fetched once per indicator)
  useEffect(() => {
    if (!iso3 || monthlyInd in forecasts) return;
    let on = true;
    pulseGet("forecast", { indicator: monthlyInd, country: iso3, lang })
      .then((d) => { if (on) setForecasts((f) => ({ ...f, [monthlyInd]: d?.forecast || null })); })
      .catch(() => { if (on) setForecasts((f) => ({ ...f, [monthlyInd]: null })); });
    return () => { on = false; };
  }, [iso3, monthlyInd, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyInds = useMemo(() => ["nights_total", "nights_foreign", "arrivals_monthly", "arrivals_monthly_foreign"]
    .filter((k) => (monthly?.series?.[k] || []).length), [monthly]);
  const monthlyRows = useMemo(() => {
    const pts = monthly?.series?.[monthlyInd] || [];
    const obs = pts.slice(-24).map((p) => ({ period: fmtPeriod(p.period, lang), raw: p.period, value: p.value, yoy: p.yoy_pct }));
    return withForecastRows(obs, forecast, lang);
  }, [monthly, monthlyInd, forecast, lang]);

  const mixRows = useMemo(() => {
    const tot = monthly?.series?.nights_total || [];
    const forn = monthly?.series?.nights_foreign || [];
    if (!tot.length || !forn.length) return [];
    const fMap = Object.fromEntries(forn.map((p) => [p.period, p.value]));
    return tot.slice(-24)
      .filter((p) => fMap[p.period] !== undefined && fMap[p.period] <= p.value)
      .map((p) => ({
        period: fmtPeriod(p.period, lang),
        foreign: fMap[p.period],
        domestic: Math.max(0, p.value - fMap[p.period]),
      }));
  }, [monthly, lang]);

  const receipts = rcSrc === "usd" ? k.travel_receipts_usd : k.travel_receipts_ncu;
  const receiptsValue = receipts
    ? (rcSrc === "usd" ? receipts.value : applyMult(receipts.value, receipts.unit_mult))
    : null;

  const tabs = (
    <div className="flex items-center gap-1 mb-6" role="tablist" aria-label={label} style={{ borderBottom: `1px solid ${T.border}` }}>
      {[
        { key: "overview", to: `/pulse/country/${iso3}`, label: t("tab_overview"), testid: "country-tab-overview" },
        { key: "seasonality", to: `/pulse/country/${iso3}?view=seasonality`, label: t("tab_seasonality"), testid: "country-tab-seasonality" },
      ].map((tb) => (
        <Link key={tb.key} to={withLang(tb.to)} role="tab" aria-selected={view === tb.key} data-testid={tb.testid}
          className="text-xs font-bold uppercase tracking-wider px-3 py-2.5"
          style={{
            color: view === tb.key ? T.accent : T.muted,
            borderBottom: `2px solid ${view === tb.key ? T.brand : "transparent"}`,
            marginBottom: -1,
          }}>
          {tb.label}
        </Link>
      ))}
    </div>
  );

  return (
    <PulseLayout active="world">
      <div className="pulse-fade-up">
        <Link to={withLang("/pulse")} className="inline-flex items-center gap-1.5 text-xs font-semibold mb-4"
          style={{ color: T.muted }} data-testid="country-back-link">
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> {t("back_world")}
        </Link>
        <h1 className="pulse-serif text-4xl sm:text-5xl font-medium mb-6 flex items-center gap-3 flex-wrap" data-testid="country-pulse-title">
          {label}
          {(monthly?.area_note || monthly?.series_break) && (
            <span className="font-sans inline-flex items-center gap-2 tracking-normal" data-testid="country-title-badges">
              {monthly?.area_note && <NoteBadge note={monthly.area_note} dm={dm} label={t("note_badge_tur")} testid="country-note-badge" />}
              {monthly?.series_break && <BreakBadge brk={monthly.series_break} dm={dm} short testid="country-break-badge" />}
            </span>
          )}
        </h1>
        {tabs}

        {notFound && <NoData dm={dm} h={200} msg={t("no_data_country")} testid="country-not-found" />}
        {!notFound && view === "seasonality" && <SeasonalityView iso3={iso3} label={label} dm={dm} />}
        {!notFound && view === "overview" && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-8"
              style={{ gap: 1, background: T.border, border: `1px solid ${T.border}` }}>
              {loading && [...Array(6)].map((_, i) => <SkeletonCard key={i} h={170} dm={dm} />)}
              {!loading && k.intl_arrivals && (
                <KpiCard dm={dm} testid="kpi-card-arrivals"
                  label={t("kpi_arrivals")} tip={t("kpi_arrivals_tip")}
                  value={k.intl_arrivals.value} yoy={k.intl_arrivals.yoy_pct}
                  period={k.intl_arrivals.period} source={k.intl_arrivals.source_org}
                  dataset={k.intl_arrivals.dataset_code} spark={sparks.intl_arrivals}
                  badge={k.recovery_vs_2019 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5" title={t("kpi_recovery_tip")}
                      style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>
                      {Math.round(k.recovery_vs_2019.value)}% {t("vs2019")}
                    </span>
                  )} />
              )}
              {!loading && receipts && (
                <KpiCard dm={dm} testid="kpi-card-receipts"
                  label={t("kpi_receipts")} tip={t("kpi_receipts_tip")}
                  value={receiptsValue} unit={rcSrc === "usd" ? "US$" : (receipts.unit || "")}
                  yoy={receipts.yoy_pct} period={receipts.period}
                  source={receipts.source_org} dataset={receipts.dataset_code}
                  spark={rcSrc === "usd" ? sparks.travel_receipts_usd : undefined}
                  extra={(k.travel_receipts_usd && k.travel_receipts_ncu) ? (
                    <div className="flex items-center gap-1" role="group" aria-label={t("cmp_source")} data-testid="receipts-source-switch">
                      {[["usd", t("kpi_receipts_usd")], ["ncu", t("kpi_receipts_ncu")]].map(([v, lbl]) => (
                        <button key={v} onClick={() => setRcSrc(v)} aria-pressed={rcSrc === v}
                          data-testid={`receipts-source-${v}`}
                          className="text-[10px] font-semibold px-1.5 py-0.5"
                          style={{
                            background: rcSrc === v ? T.brand : "transparent",
                            color: rcSrc === v ? "#fff" : T.muted,
                            border: `1px solid ${rcSrc === v ? T.brand : T.border}`, borderRadius: 2,
                          }}>{lbl}</button>
                      ))}
                    </div>
                  ) : undefined} />
              )}
              {!loading && k.receipts_per_arrival_usd && (
                <KpiCard dm={dm} testid="kpi-card-rpa"
                  label={t("kpi_rpa")} tip={t("kpi_rpa_tip")}
                  value={`$${fmtCompact(k.receipts_per_arrival_usd.value, lang)}`}
                  period={k.receipts_per_arrival_usd.period}
                  source={k.receipts_per_arrival_usd.source_org} />
              )}
              {!loading && k.tourism_gdp_share && (
                <KpiCard dm={dm} testid="kpi-card-gdp"
                  label={t("kpi_gdp")} tip={t("kpi_gdp_tip")}
                  value={fmtPct(k.tourism_gdp_share.value, lang, false)}
                  yoy={undefined} period={k.tourism_gdp_share.period}
                  source={k.tourism_gdp_share.source_org} dataset={k.tourism_gdp_share.dataset_code}
                  spark={sparks.tourism_gdp_share} />
              )}
              {!loading && k.tourism_employment_share && (
                <KpiCard dm={dm} testid="kpi-card-employment"
                  label={t("kpi_emp")} tip={t("kpi_emp_tip")}
                  value={fmtPct(k.tourism_employment_share.value, lang, false)}
                  period={k.tourism_employment_share.period}
                  source={k.tourism_employment_share.source_org} dataset={k.tourism_employment_share.dataset_code}
                  spark={sparks.tourism_employment_share} />
              )}
              {!loading && kpi?.v2?.origin_top3_share_pct && (
                <KpiCard dm={dm} testid="kpi-card-top3-origin"
                  label={t("kpi_top3")} tip={t("kpi_top3_tip")}
                  value={fmtPct(kpi.v2.origin_top3_share_pct.value, lang, false)}
                  period={kpi.v2.origin_top3_share_pct.period}
                  source={kpi.v2.origin_top3_share_pct.source_org} dataset={kpi.v2.origin_top3_share_pct.dataset_code}
                  extra={(
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="kpi-top3-list">
                      {kpi.v2.origin_top3_share_pct.top3.map((o) => (
                        <span key={o.iso3} className="text-[10px] font-semibold px-1.5 py-0.5"
                          style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>{regionName(o, lang)}</span>
                      ))}
                      {kpi.v2.origin_hhi && (
                        <span className="text-[10px] pulse-num" style={{ color: T.muted }} title={t("kpi_top3_tip")}>
                          {t("kpi_top3_hhi")} {Math.round(kpi.v2.origin_hhi.value)}
                        </span>
                      )}
                    </div>
                  )} />
              )}
              {!loading && k.monthly_nights_ytd && (
                <KpiCard dm={dm} testid="kpi-card-nights-ytd"
                  label={`${t("kpi_nights_ytd")} (${k.monthly_nights_ytd.year})`} tip={t("kpi_nights_ytd_tip")}
                  value={k.monthly_nights_ytd.value} yoy={k.monthly_nights_ytd.yoy_pct}
                  period={k.monthly_nights_ytd.latest_month}
                  source={k.monthly_nights_ytd.source_org} dataset={k.monthly_nights_ytd.dataset_code} />
              )}
            </div>

            {/* Insight strip — template-based factual sentences, zero LLM */}
            {!loading && kpi?.insights?.length > 0 && (
              <section className="mb-8 p-4" data-testid="insight-strip"
                style={{ background: T.surface, borderLeft: `3px solid ${T.brand}`, border: `1px solid ${T.border}`, borderLeftWidth: 3, borderLeftColor: T.brand }}>
                <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: T.muted }}>{t("insights_title")}</p>
                <ul className="flex flex-col gap-1.5">
                  {kpi.insights.map((s, i) => (
                    <li key={i} className="pulse-serif text-base leading-relaxed" style={{ color: T.text }}>{s}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Monthly strip */}
            <section className="mb-8">
              <SectionTitle dm={dm}>{t("monthly_title")}{forecast && forecast.display_rule !== "hide" ? ` ${t("fc_horizon_note", { n: forecast.points.length })}` : ""}</SectionTitle>
              {loading && <SkeletonCard h={220} dm={dm} />}
              {!loading && !monthlyRows.length && (
                <NoData dm={dm} h={140} msg={t("no_monthly", { country: label })} testid="monthly-no-data" />
              )}
              {!loading && monthlyInds.length > 1 && (
                <div className="flex items-center gap-1 mb-3 flex-wrap" role="tablist" aria-label={t("monthly_title")}>
                  {monthlyInds.map((k) => (
                    <button key={k} role="tab" aria-selected={monthlyInd === k} onClick={() => setMonthlyInd(k)}
                      data-testid={`monthly-ind-${k}`} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5"
                      style={{ background: monthlyInd === k ? T.brand : T.surface, color: monthlyInd === k ? "#fff" : T.muted,
                        border: `1px solid ${monthlyInd === k ? T.brand : T.border}`, borderRadius: 2 }}>
                      {t(`mind_${k}`)}
                    </button>
                  ))}
                </div>
              )}
              {!loading && monthlyRows.length > 0 && (
                <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="monthly-chart">
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={monthlyRows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                        <CartesianGrid stroke={T.border} vertical={false} />
                        <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.muted }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: T.border }} />
                        <YAxis tickFormatter={(v) => fmtCompact(v, lang)} tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} />
                        <Tooltip content={<ForecastTooltip dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
                        <Bar dataKey="value" name={t(`mind_${monthlyInd}`)} fill={T.brand} isAnimationActive={true} animationDuration={400} />
                        {forecastLayers(forecast, t)}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2"><SourceChip source="EUROSTAT" dataset={monthlyInd.startsWith("arrivals") ? "tour_occ_arm" : "tour_occ_nim"} period={(monthly?.series?.[monthlyInd] || []).slice(-1)[0]?.period} dm={dm} /></div>
                  <ForecastNote fc={forecast} dm={dm} testid="country-forecast-note" />
                  <SrTable caption={t("monthly_title")} columns={[t("rank_col_year"), t(`mind_${monthlyInd}`), t("yoy"), t("fc_median"), t("fc_band")]}
                    rows={monthlyRows.map((r) => [r.period, fmtCompact(r.value, lang), fmtPct(r.yoy, lang), fmtCompact(r.fq50, lang),
                      r.band ? `${fmtCompact(r.band[0], lang)} – ${fmtCompact(r.band[1], lang)}` : "—"])} />
                </div>
              )}
            </section>

            <OriginMixSection iso3={iso3} dm={dm} />

            {/* Markets + mix */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
              <section>
                <SectionTitle dm={dm}>{t("markets_title")}</SectionTitle>
                {loading && <SkeletonCard h={280} dm={dm} />}
                {!loading && (!markets || !markets.markets?.length) && <NoData dm={dm} h={140} testid="markets-no-data" />}
                {!loading && markets?.markets?.length > 0 && (
                  <div style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="markets-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: T.muted, borderBottom: `2px solid ${T.border}` }}>
                          <th className="px-4 py-2 font-bold" scope="col">{t("markets_col_market")}</th>
                          <th className="py-2 text-right font-bold" scope="col">{t("markets_col_arrivals")}</th>
                          <th className="py-2 text-right font-bold" scope="col">{t("markets_col_share")}</th>
                          <th className="px-4 py-2 text-right font-bold" scope="col">{t("yoy")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {markets.markets.map((m) => (
                          <tr key={m.counterpart_area} className="pulse-row-hover" style={{ borderTop: `1px solid ${T.border}` }}>
                            <td className="px-4 py-2 font-medium">
                              {m.label}
                              {markets.fastest_growing?.counterpart_area === m.counterpart_area && (
                                <span className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                                  style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>{t("markets_fastest")}</span>
                              )}
                            </td>
                            <td className="py-2 text-right pulse-num">{fmtCompact(m.value, lang)}</td>
                            <td className="py-2 text-right pulse-num">{m.share_pct !== null && m.share_pct !== undefined ? `${m.share_pct}%` : "—"}</td>
                            <td className="px-4 py-2 text-right"><DeltaBadge pct={m.yoy_pct} dm={dm} suffix=" " /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="px-4 py-2"><SourceChip source={markets.source_org} dataset={markets.dataset_code} period={markets.period} dm={dm} /></div>
                  </div>
                )}
              </section>

              <section>
                <SectionTitle dm={dm}>{t("mix_title")}</SectionTitle>
                {loading && <SkeletonCard h={280} dm={dm} />}
                {!loading && !mixRows.length && <NoData dm={dm} h={140} msg={t("no_monthly", { country: label })} testid="mix-no-data" />}
                {!loading && mixRows.length > 0 && (
                  <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="mix-chart">
                    <div style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={mixRows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                          <CartesianGrid stroke={T.border} vertical={false} />
                          <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.muted }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: T.border }} />
                          <YAxis tickFormatter={(v) => fmtCompact(v, lang)} tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} />
                          <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
                          <Area stackId="1" dataKey="foreign" name={t("mix_foreign")} fill={T.brand} stroke={T.brand} fillOpacity={0.85} isAnimationActive={true} animationDuration={400} />
                          <Area stackId="1" dataKey="domestic" name={t("mix_domestic")} fill={dm ? "#3B4252" : "#B7C6C8"} stroke={dm ? "#3B4252" : "#9FB3B6"} fillOpacity={0.7} isAnimationActive={true} animationDuration={400} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-[10px] mt-1.5" style={{ color: T.muted }}>{t("mix_note")}</p>
                    <div className="mt-1.5"><SourceChip source="EUROSTAT" dataset="tour_occ_nim" period={(monthly?.series?.nights_total || []).slice(-1)[0]?.period} dm={dm} /></div>
                    <SrTable caption={t("mix_title")} columns={[t("rank_col_year"), t("mix_foreign"), t("mix_domestic")]}
                      rows={mixRows.map((r) => [r.period, fmtCompact(r.foreign, lang), fmtCompact(r.domestic, lang)])} />
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </PulseLayout>
  );
}
